
import { fetchTasks, postNewTask, postUpdateTask } from "@/helpers/api";
import { Task } from "@/interfaces/Task";
import { defineComponent, toRefs, reactive, ref, PropType } from "vue";
import Datepicker from "vue3-datepicker";
import VueFeather from "vue-feather";
import { TaskRule } from "@/interfaces/TaskRule";
import { jsTimeToRealEpoch, timeAsJS, getNextDayFromDate, timeNowAsJS } from "@/helpers/time";
import { getDateFormat } from "@/helpers/store";

type TagSetDraft = {
  tags: Record<string, string>;
  selectTag: string;
  selectTagValue: string;
};

const normalizeSensorTags = (sensorTags: Record<string, string> | Record<string, string>[] | null): TagSetDraft[] => {
  if (!sensorTags || (!Array.isArray(sensorTags) && Object.keys(sensorTags).length === 0)) {
    return [{ tags: {}, selectTag: "", selectTagValue: "" }];
  }
  if (Array.isArray(sensorTags)) {
    return sensorTags.map((tags) => ({ tags: { ...tags }, selectTag: "", selectTagValue: "" }));
  }
  return [{ tags: { ...sensorTags }, selectTag: "", selectTagValue: "" }];
};

export default defineComponent({
  name: "tasks-config-modal",
  components: {
    Datepicker,
    VueFeather,
  },
  props: {
    selected_rule: {
      type: Object as PropType<TaskRule>,
      required: true,
    },
    sensorTagOptions: {
      type: Object as PropType<Record<string, string[]>>,
      required: true,
    },
    task_rules: {
      type: Object as PropType<TaskRule[]>,
      required: true,
    },
  },
  setup(props, { emit }) {
    const state = reactive({
      Name: props.selected_rule.Name,
      errorMessage: "",
      TaskID: props.selected_rule.TaskID,
      sourceData: props.selected_rule.InputTable,
      pickerStartDate: props.selected_rule.StartTime
        ? ref(timeAsJS(props.selected_rule.StartTime))
        : ref(timeNowAsJS()),
      pickerEndDate: props.selected_rule.EndTime ? ref(timeAsJS(props.selected_rule.EndTime)) : null,
      endDate: (props.selected_rule.EndTime ? true : false) || false,
      enabled: props.selected_rule.Enabled,
      multipleTruePerBatch: props.selected_rule.MultipleTruePerBatch ?? true,
      tasks: [] as Task[],
      tagSets: normalizeSensorTags(props.selected_rule.SensorTags),
      sensorTagOptions: props.sensorTagOptions as Record<string, string[]>,
      DataTaskRuleIDs: props.selected_rule.DataTaskRuleIDs,
      maxConcurrent: (props.selected_rule.MaxConcurrent ?? null) as number | string | null,
      task_rules: props.task_rules,
      parameters: null as null | Record<string, string>,
      parameterOptions: null as null | Record<string, string[]>,
      dateFormat: getDateFormat(),
    });

    const getPropertyForTask = (
      taskID: number,
      propertyName: "InputTable" | "Name" | "ParameterNames" | "TaskID" | "Documentation"
    ): string => {
      const filtered = state.tasks.filter((task) => task.TaskID === taskID);
      return filtered[0] ? String(filtered[0][propertyName]) : "";
    };

    const sortTasks = (tasks: Task[]) => {
      return tasks.sort((a: Task, b: Task) => (a.Name > b.Name ? 1 : -1));
    };

    const get_tasks = async () => {
      const data = await fetchTasks();
      state.tasks = sortTasks(data);
      state.parameterOptions = data
        .map(({ TaskID, ParameterNames }) => ({ [TaskID]: ParameterNames }))
        .reduce((a, b) => ({ ...a, ...b }), {});
      if (state.TaskID !== null) {
        updateParameterOptions(state.TaskID);
      }
    };

    const updateParameterOptions = (TaskID: number) => {
      if (state.parameterOptions) {
        let newParameters = state.parameterOptions[TaskID].reduce((a, b) => ({ ...a, [b]: null }), {}) as Record<
          string,
          string
        >;
        Object.keys(newParameters).map((key) => {
          if (key in props.selected_rule.Parameters) {
            newParameters[key] = JSON.stringify(props.selected_rule.Parameters[key]);
          }
        });
        state.parameters = newParameters;
      }
    };

    const isValidJSON = (jsonInput: string) => {
      try {
        JSON.parse(jsonInput);
        return true;
      } catch (error) {
        return false;
      }
    };

    /**
     * Takes null or parameters object with strings or null as values and returns parameters as JSON.
     * Removes empty parameters.
     */
    const formattedParameters = (parameters: null | Record<string, string>) =>
      parameters === null
        ? {}
        : Object.entries(parameters).reduce((a, [b, c]) => (c !== null ? { ...a, [b]: JSON.parse(c) } : a), {});

    // Returns the tag keys not yet used in this set's draft, for the add-row dropdown.
    const filteredTagOptionsForSet = (draft: TagSetDraft): string[] =>
      Object.keys(state.sensorTagOptions)
        .filter((a) => !(a in draft.tags))
        .sort((a, b) => (a > b ? 1 : -1));

    const addRuleTag = (draft: TagSetDraft) => {
      draft.tags[draft.selectTag] = draft.selectTagValue;
      draft.selectTag = "";
      draft.selectTagValue = "";
    };

    const addTagSet = () => {
      state.tagSets.push({ tags: {}, selectTag: "", selectTagValue: "" });
    };

    const removeTagSet = (index: number) => {
      state.tagSets.splice(index, 1);
    };

    const clearSelectedRule = () => {
      emit("clearSelectedRule", null);
    };

    const save_rule = async () => {
      state.errorMessage = "";
      let newParameters = {};
      try {
        newParameters = formattedParameters(state.parameters);
      } catch (error) {
        state.errorMessage = "Invalid JSON for parameters";
        return;
      }

      if (!state.Name) {
        state.errorMessage = "You must specify a name";
        return;
      }
      if (!state.TaskID) {
        state.errorMessage = "You must choose a task";
        return;
      }
      if (state.tagSets.some((d) => d.selectTag || d.selectTagValue)) {
        state.errorMessage = "You have not finished adding a sensor tag";
        return;
      }

      // Discard empty sets, then send a plain dict for one set or an array for multiple.
      const nonEmpty = state.tagSets.map((d) => d.tags).filter((t) => Object.keys(t).length > 0);
      const SensorTags = nonEmpty.length === 0 ? null : nonEmpty.length === 1 ? nonEmpty[0] : nonEmpty;

      const isNewRule = props.selected_rule.TaskRuleID === null;

      let newTask = {
        Name: state.Name,
        TaskID: state.TaskID,
        SensorIDs: props.selected_rule.SensorIDs,
        SensorTags,
        DataTaskRuleIDs: state.DataTaskRuleIDs,
        StartTime: jsTimeToRealEpoch(state.pickerStartDate),
        Enabled: state.enabled,
        MultipleTruePerBatch: state.multipleTruePerBatch,
        Parameters: newParameters,
        MaxConcurrent:
          state.maxConcurrent === "" || state.maxConcurrent === null ? null : Number(state.maxConcurrent),
      } as TaskRule;

      if (state.endDate && state.pickerEndDate) {
        newTask.EndTime = jsTimeToRealEpoch(state.pickerEndDate);
      } else if (!state.endDate && props.selected_rule.EndTime) {
        newTask.EndTime = null;
      }

      try {
        if (isNewRule) {
          await postNewTask(newTask);
        } else {
          if (!props.selected_rule.TaskRuleID) {
            state.errorMessage = "You've not supplied a task rule ID";
            return;
          }
          await postUpdateTask(newTask, props.selected_rule.TaskRuleID);
        }
      } catch (error) {
        if (error instanceof SyntaxError) {
          state.errorMessage = "Syntax error";
        } else if (error instanceof Error) {
          state.errorMessage = `Error adding/updating task: ${error.message ? error.message : ""}`;
          return;
        }
      }
      emit("getTaskRules");
      emit("clearSelectedRule", null);
    };

    const push_end_date = () => {
      if (state.pickerEndDate) {
        if (state.pickerStartDate > state.pickerEndDate) {
          state.pickerEndDate = getNextDayFromDate(state.pickerStartDate);
        }
      }
    };

    get_tasks();

    return {
      ...toRefs(state),
      filteredTagOptionsForSet,
      addRuleTag,
      addTagSet,
      removeTagSet,
      clearSelectedRule,
      save_rule,
      getPropertyForTask,
      updateParameterOptions,
      isValidJSON,
      push_end_date,
    };
  },
});
